import { forwardRef, useState } from "react";
import type { Lang, Question } from "../types.ts";
import type { Settings } from "../state/store.ts";
import { pick } from "../catalog.ts";
import { t } from "../i18n/ui.ts";
import { LocalizedText, htmlLangOf } from "./Localized.tsx";
import { MediaView } from "./MediaView.tsx";

interface Props {
  question: Question;
  settings: Settings;
  selected: number[];
  typed: string;
  onToggle: (index: number) => void;
  onType: (value: string) => void;
  /** Show which answers were right once the user has committed. */
  revealed: boolean;
  showExplanation: boolean;
  /**
   * Reveal the secondary language on the question and answers. Off by default
   * so answering mirrors the real exam; the explanation always shows both.
   */
  showSecondary?: boolean;
  /** Rendered in the header bar, e.g. "4 / 30". */
  positionLabel?: string;
  playSignal?: number;
  disabled?: boolean;
  /** Closes the sheet; renders the header's ✕ when provided. */
  onClose?: () => void;
  /** Makes the header's ⓘ a button that opens the question-detail sheet. */
  onInfo?: () => void;
}

/**
 * The question panel, styled to match the official theory-test screen: green
 * header strip, question across the top, media on the left and answers on the
 * right. Structure is semantic (fieldset/legend/checkbox) so the look is a skin
 * rather than a re-implementation of form controls.
 */
export const QuestionCard = forwardRef<HTMLDivElement, Props>(function QuestionCard(
  {
    question, settings, selected, typed, onToggle, onType,
    revealed, showExplanation, showSecondary = false, positionLabel, playSignal,
    disabled = false, onClose, onInfo,
  },
  ref
) {
  const lang: Lang = settings.primary;
  // While answering, the second language is opt-in; the explanation ignores this.
  const secondary = showSecondary ? settings.secondary : null;
  const isMulti = question.answers.filter((a) => a.correct).length > 1;

  /**
   * Film questions run in two steps, as in the real exam: watch the clip, then
   * answer from memory with the clip gone. Reviewing a finished exam skips the
   * gate — there the point is to see what was asked.
   */
  const isFilm = question.media?.kind === "video" && !disabled;
  const [filmWatched, setFilmWatched] = useState(false);
  const [pastGate, setPastGate] = useState(false);
  const showQuestion = !isFilm || pastGate;

  return (
    <div className="tq" ref={ref} tabIndex={-1}>
      <div className="tq__header">
        {onInfo ? (
          <button
            type="button"
            className="tq__info tq__info--button"
            onClick={onInfo}
            aria-label={t(lang, "questionDetail")}
          >
            i
          </button>
        ) : (
          <span className="tq__info" aria-hidden="true">i</span>
        )}
        <span className="tq__number" translate="no">{question.number}</span>
        {/* Which language you are reading, as on the official mobile screen. */}
        <span className="tq__lang" translate="no">{lang.toUpperCase()}</span>
        {positionLabel && <span className="tq__pos-label">{positionLabel}</span>}
        <span className="tq__points">
          {t(lang, "pointsShort")}: {question.points}
        </span>
        {onClose && (
          <button type="button" className="tq__close" onClick={onClose} aria-label={t(lang, "close")}>
            ✕
          </button>
        )}
      </div>

      {/* Only this scrolls. The green header above and the toolbar below the
          card stay put, as they do in the official app. */}
      <div className="tq__scroll">
        {showQuestion ? (
          <>
            <div className="tq__text">
              <LocalizedText
                value={question.text}
                primary={settings.primary}
                secondary={secondary}
                as="p"
              />
            </div>

            <div
              className={`tq__main ${
                question.media && !isFilm ? "" : "tq__main--noMedia"
              }`}
            >
              {question.media && !isFilm && (
                <div className="tq__media">
                  <MediaView
                    media={question.media}
                    number={question.number}
                    lang={lang}
                    playSignal={playSignal}
                  />
                </div>
              )}

              <div className="tq__answerCol">
                {question.kind === "number" ? (
                  <NumericAnswer
                    question={question}
                    settings={settings}
                    secondary={secondary}
                    typed={typed}
                    onType={onType}
                    revealed={revealed}
                    disabled={disabled}
                  />
                ) : (
                  <ChoiceAnswers
                    question={question}
                    settings={settings}
                    secondary={secondary}
                    selected={selected}
                    onToggle={onToggle}
                    revealed={revealed}
                    isMulti={isMulti}
                    disabled={disabled}
                  />
                )}
              </div>
            </div>

            {showExplanation && pick(question.explanation, lang) && (
              <section className="tq__explanation" aria-label={t(lang, "explanation")}>
                <h3>{t(lang, "explanation")}</h3>
                <LocalizedText
                  value={question.explanation}
                  primary={settings.primary}
                  secondary={settings.secondary}
                  as="div"
                  html
                />
                {question.stvo && <p className="tq__stvo" translate="no">StVO § {question.stvo}</p>}
              </section>
            )}
          </>
        ) : (
          <>
            <div className="tq__text">
              <p className="loc loc--primary">{t(lang, "watchFilmPrompt")}</p>
            </div>

            <div className="tq__film">
              <MediaView
                media={question.media!}
                number={question.number}
                lang={lang}
                playSignal={playSignal}
                onWatched={() => setFilmWatched(true)}
              />

              <button
                type="button"
                className="btn btn--primary tq__toAnswers"
                onClick={() => setPastGate(true)}
                disabled={!filmWatched}
              >
                {t(lang, "toAnswers")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
});

function ChoiceAnswers({
  question, settings, secondary, selected, onToggle, revealed, isMulti, disabled,
}: {
  question: Question;
  settings: Settings;
  secondary: Lang | null;
  selected: number[];
  onToggle: (i: number) => void;
  revealed: boolean;
  isMulti: boolean;
  disabled: boolean;
}) {
  const lang = settings.primary;
  const pretext = pick(question.pretext, lang);

  return (
    <fieldset className="tqans" disabled={disabled}>
      <legend className="tqans__legend">
        {pretext ? (
          <LocalizedText
            value={question.pretext}
            primary={settings.primary}
            secondary={secondary}
            as="span"
            className="tqans__pretext"
          />
        ) : (
          <span className="sr-only">{t(lang, "yourAnswer")}</span>
        )}
        {isMulti && <span className="tqans__hint">{t(lang, "selectAll")}</span>}
      </legend>

      {question.answers.map((answer, i) => {
        const checked = selected.includes(i);
        const state = !revealed
          ? undefined
          : answer.correct
            ? "correct"
            : checked
              ? "wrong"
              : undefined;

        return (
          <label
            key={i}
            className={["tqans__item", state && `tqans__item--${state}`]
              .filter(Boolean)
              .join(" ")}
          >
            <input
              type="checkbox"
              className="tqans__input"
              checked={checked}
              onChange={() => onToggle(i)}
            />
            <span className="tqans__box" aria-hidden="true">
              <span className="tqans__key">{i + 1}</span>
            </span>
            <span className="tqans__text">
              <LocalizedText
                value={answer.text}
                primary={settings.primary}
                secondary={secondary}
                as="span"
                html
              />
              {state && (
                <span className="tqans__state">
                  {t(lang, state === "correct" ? "correctAnswer" : "incorrect")}
                </span>
              )}
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}

function NumericAnswer({
  question, settings, secondary, typed, onType, revealed, disabled,
}: {
  question: Question;
  settings: Settings;
  secondary: Lang | null;
  typed: string;
  onType: (v: string) => void;
  revealed: boolean;
  disabled: boolean;
}) {
  const lang = settings.primary;
  const id = `num-${question.id}`;
  const unit = pick(question.numeric?.unit ?? {}, lang);
  const pretext = pick(question.pretext, lang);

  return (
    <div className="tqnum">
      <label htmlFor={id} className="tqnum__label">
        {pretext ? (
          <LocalizedText
            value={question.pretext}
            primary={settings.primary}
            secondary={secondary}
            as="span"
          />
        ) : (
          t(lang, "enterNumber")
        )}
      </label>
      <span className="tqnum__field">
        <input
          id={id}
          type="text"
          inputMode="decimal"
          autoComplete="off"
          value={typed}
          disabled={disabled}
          onChange={(e) => onType(e.target.value)}
          aria-label={t(lang, "yourAnswer")}
        />
        {unit && <span className="tqnum__unit" lang={htmlLangOf(lang)}>{unit}</span>}
      </span>
      {revealed && (
        <p className="tqnum__solution">
          {t(lang, "correctAnswer")}: <strong>{question.numeric?.value} {unit}</strong>
        </p>
      )}
    </div>
  );
}
